import React, { useEffect, useState } from 'react';
import styled from 'styled-components/macro';
import { Redirect, useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { translations } from 'locales/translations';

import { AuthCheckStatus, BrandName } from 'utils/types';
import { humanReadableInterval } from 'utils/date';

import Auth from 'app/api/Auth';

import LoadingSpinner from 'app/components/Loading/LoadingSpinner';

interface Props {
    children?: React.ReactNode;
}

interface StyleProps {
    showDisplay: boolean;
}

export default function AuthCheck(props: Props) {
    let location = useLocation();
    let history = useHistory();
    let current = Auth.current();

    const [authStatus, setAuthStatus] = useState<AuthCheckStatus>(AuthCheckStatus.OK);
    const [expiresInSeconds, setExpiresInSeconds] = useState(0);

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { t, i18n } = useTranslation();

    useEffect(() => {
        setAuthStatus(AuthCheckStatus.OK);

        const authInterval = setInterval(() => {
            const heartbeat = Auth.heartbeat();

            let newAuthStatus = AuthCheckStatus.OK;
            if (authStatus === AuthCheckStatus.REFRESHING) newAuthStatus = AuthCheckStatus.REFRESHING;

            if (!heartbeat.current) {
                newAuthStatus = AuthCheckStatus.EXPIRED;
            } else if (heartbeat.expiresIn < 300000) {
                setExpiresInSeconds(heartbeat.expiresIn);
                newAuthStatus = AuthCheckStatus.SOON;
            }

            setAuthStatus(newAuthStatus);
        }, 5000);

        return () => {
            if (authInterval) clearInterval(authInterval);
        };
        // don't want useEffects to react to authStatus changes only want to apply
        // changes on heartbeat, so leave off dependencies list
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location]);

    const promptClickHandler = e => {
        if (authStatus === AuthCheckStatus.EXPIRED) {
            loginRedirect();
        } else {
            setAuthStatus(AuthCheckStatus.REFRESHING);
            Auth.refresh()
                .then(result => {
                    if (result) {
                        setAuthStatus(AuthCheckStatus.OK);
                    } else {
                        loginRedirect();
                    }
                })
                .catch(error => {
                    console.log('error', error);
                    loginRedirect();
                });
        }
    };

    const loginRedirect = () => {
        let brand = Auth.getCurrentBrandName();
        setAuthStatus(AuthCheckStatus.OK);
        history.push(`/login/${brand}`);
    };

    const isOK = authStatus === AuthCheckStatus.OK;
    let displayMessage = '';

    if (!isOK) {
        if (authStatus === AuthCheckStatus.SOON) {
            displayMessage = t(translations.Auth.expires_soon, { seconds: humanReadableInterval(expiresInSeconds) });
        } else if (authStatus === AuthCheckStatus.EXPIRED) {
            displayMessage = t(translations.Auth.expired);
        } else if (authStatus === AuthCheckStatus.REFRESHING) {
            displayMessage = t(translations.Auth.refreshing);
        }
    }

    let isLoginPage = location.pathname.indexOf('/login') === 0;

    if (isLoginPage === false && !current) {
        let loginBrand: BrandName | null = Auth.getCurrentBrandName();
        if (loginBrand === null) loginBrand = BrandName.bzi;
        return <Redirect to={`/login/${loginBrand}`} />;
    } else {
        return (
            <AuthWrapper>
                {!isLoginPage ? (
                    <>
                        <AuthPrompt showDisplay={!isOK}>
                            {!isOK ? <AuthPromptText onClick={promptClickHandler} dangerouslySetInnerHTML={{ __html: displayMessage }}></AuthPromptText> : null}
                            {authStatus === AuthCheckStatus.REFRESHING ? (
                                <LoadingWrapper>
                                    <LoadingSpinner />
                                </LoadingWrapper>
                            ) : null}
                        </AuthPrompt>
                        {authStatus === AuthCheckStatus.EXPIRED ? <AuthCover /> : null}
                    </>
                ) : null}
                {props.children}
            </AuthWrapper>
        );
    }
}

const AuthWrapper = styled.div`
    position: relative;
`;

const AuthPrompt = styled.div<StyleProps>`
    display: flex;
    flex-direction: row;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 50%;
    z-index: 99;
    background: var(--color-dark-blue);
    padding: 15px 25px;
    border-bottom-left-radius: 20px;
    border-bottom-right-radius: 20px;
    overflow: hidden;
    width: 75%;
    max-width: 500px;
    font-size: 14px;
    line-height: 24px;
    color: white;
    text-align: center;
    transition: transform 500ms ease;
    transform: ${props => (props.showDisplay ? 'translate(-50%, 0)' : 'translate(-50%, -200%)')};
`;

const LoadingWrapper = styled.div`
    position: relative;
    width: 30px;
    height: 1px;
    transform: scale(0.5) translateY(10px);
`;

const AuthPromptText = styled.div`
    position: relative;

    span {
        text-decoration: underline;
        cursor: pointer;
    }
`;

const AuthCover = styled.div`
    position: fixed;
    z-index: 98;
    background: rgba(255, 255, 255, 0.3);
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
`;
